import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';

import type { Booking } from '../api/schemas';
import { BOOKING_STATUS_LABEL, BOOKING_STATUS_TONE } from '../lib/bookings';
import { formatMoneyCents, formatTime } from '../lib/format';

type AgendaRowProps = {
  booking: Booking;
  /** The line that leads the row: the customer when known, else the service. */
  title: string;
  /** Facts under the title, already resolved to words: service, duración, personas, quién atiende. */
  facts: string[];
  /** 'Ayer' or a date word when the row sits outside its own day's section. */
  dayNote?: string;
  /** Cue banner and row actions render under the link so the row itself stays one tap target. */
  children?: ReactNode;
};

/**
 * One reservation as one line of the agenda: time leads, the person leads the
 * text, the status is a word. The whole row is the link to the detail; any
 * action lives under it, never inside it.
 */
export function AgendaRow({ booking, title, facts, dayNote, children }: AgendaRowProps) {
  const settled = booking.status === 'completed' || booking.status === 'paid';
  return (
    <li className="agenda-item" data-past={settled || undefined}>
      <Link
        to="/reservas/$bookingId"
        params={{ bookingId: booking.id }}
        className="agenda-row"
        aria-label={`${formatTime(booking.startsAt)} ${title}`}
      >
        <time className="agenda-time" dateTime={booking.startsAt}>
          {formatTime(booking.startsAt)}
          {dayNote ? <span className="agenda-daynote">{dayNote}</span> : null}
        </time>
        <span className="grow">
          <span className="row-title">{title}</span>
          <span className="row-sub">{facts.join(' · ')}</span>
        </span>
        <span className="agenda-end">
          <span className="status" data-tone={BOOKING_STATUS_TONE[booking.status]}>
            {BOOKING_STATUS_LABEL[booking.status]}
          </span>
          {booking.amountCents > 0 ? (
            <span className="amount agenda-amount">{formatMoneyCents(booking.amountCents)}</span>
          ) : null}
          {booking.rating ? (
            <span className="agenda-stars" aria-label={`Calificó ${booking.rating.stars} de 5`}>
              ★ {booking.rating.stars}
            </span>
          ) : null}
        </span>
      </Link>
      {children ? <div className="agenda-under">{children}</div> : null}
    </li>
  );
}
